import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

/**
 * HlsVideoPlayer
 * - Plays an HLS manifest (m3u8) in a normal <video> tag using hls.js (or native HLS on Safari).
 * - Autoplay attempts (unmuted -> muted fallback).
 * - Shows a Loading overlay until video can play.
 */
export default function HlsVideoPlayer({
  manifestSrc,
  className = "",
  onEnded,
  autoPlay = true,
  controls = true,
  playsInline = true,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [needsTap, setNeedsTap] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !manifestSrc) return;

    setLoading(true);
    setNeedsTap(false);

    // Warm-up fetch (helps first load a bit)
    fetch(manifestSrc, { mode: "cors" }).catch(() => {});

    // Cleanup previous
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }
    try {
      video.pause();
    } catch {}
    video.removeAttribute("src");
    video.load();

    const attachNativeOrHls = () => {
      // Safari / iOS often supports HLS natively
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = manifestSrc;
        return;
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          // Stability + smoother ABR
          enableWorker: true,
          lowLatencyMode: false,

          // Avoid massive buffering but keep it smooth
          maxBufferLength: 30,
          backBufferLength: 30,

          // ABR starts conservative; this nudges it a bit without going crazy
          abrEwmaDefaultEstimate: 1_500_000, // ~1.5 Mbps starting guess
        });

        hls.loadSource(manifestSrc);
        hls.attachMedia(video);
        hlsRef.current = hls;
        return;
      }

      // Last resort
      video.src = manifestSrc;
    };

    attachNativeOrHls();

    const tryAutoPlay = async () => {
      if (!autoPlay) return;

      setNeedsTap(false);

      // Try unmuted first
      try {
        video.muted = false;
        await video.play();
        return;
      } catch {}

      // Fallback: try muted autoplay
      try {
        video.muted = true;
        await video.play();
        return;
      } catch {}

      // Autoplay blocked → user must tap
      setNeedsTap(true);
    };

    const t = setTimeout(() => {
      tryAutoPlay();
    }, 50);

    return () => {
      clearTimeout(t);
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {}
        hlsRef.current = null;
      }
    };
  }, [manifestSrc, autoPlay]);

  return (
    <div className="relative w-full h-full">
      <video
        ref={videoRef}
        className={className}
        controls={controls}
        playsInline={playsInline}
        preload="auto"
        onEnded={onEnded}
        onCanPlay={() => setLoading(false)}
        onPlaying={() => setLoading(false)}
        onWaiting={() => setLoading(true)}
      />

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/35 pointer-events-none">
          <div className="px-4 py-2 rounded-full bg-white/90 text-sm font-extrabold border shadow">
            Loading video…
          </div>
        </div>
      )}

      {needsTap && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <button
            onClick={async () => {
              const v = videoRef.current;
              if (!v) return;
              try {
                v.muted = false;
                await v.play();
                setNeedsTap(false);
              } catch {
                // user can press play in controls if needed
              }
            }}
            className="px-5 py-3 rounded-full bg-white/90 backdrop-blur text-base font-extrabold border shadow"
          >
            Tap to Play ▶️
          </button>
        </div>
      )}
    </div>
  );
}
